// Import System requirements
import Vue from 'vue'
import VueRouter from 'vue-router'
import VueTimepicker from 'vue2-timepicker'
import VueGtag from 'vue-gtag'
import { RecycleScroller } from 'vue-virtual-scroller'
import './assets/css/style.sass'
import 'vue2-timepicker/dist/VueTimepicker.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import { sync } from 'vuex-router-sync'
import axios from 'axios'
import { getSharedToken, removeSharedToken } from './assets/js/shared-token.js'
import { TOKEN_INVALID_CODES, isTokenInvalid } from './assets/js/token-error.js'
import routes from './routes'
import store from './store'

// Import Helpers for filters
import {
  domain,
  count,
  prettyDate,
  pluralize,
  deliveryName,
  orderStatusName,
  orderStateColor,
  shippingStatusName,
  shippingStateColor,
  shippingVendorName,
  tradeName,
  cityCode,
  disItemSrcType,
  disItemStatus,
  disItemActionType
} from './filters'

// Import Views - Top level
import AppView from './components/App.vue'
// import multiselect-listbox css
import 'vue-multiselect-listbox/dist/vue-multi-select-listbox.css'
// Import vue-select
import vSelect from 'vue-select'
Vue.component('v-select', vSelect)
import Multiselect from 'vue-multiselect'
import FunctionalCalendar from 'vue-functional-calendar'
Vue.use(FunctionalCalendar, {
  dayNames: ['一', '二', '三', '四', '五', '六', '日'],
  monthNames: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
})
Vue.component('multiselect', Multiselect)
import 'multiselect-two-sides'
import MutiSelectTwo from './components/elements/jMutiSelect.vue'
Vue.component('muti-select-two', MutiSelectTwo)
import MutiSelectTwoV2 from './components/elements/jMutiSelect_v2.vue'
Vue.component('muti-select-two-v2', MutiSelectTwoV2)
// multiselect
import vMultiselectListbox from 'vue-multiselect-listbox'
Vue.component('v-multiselect-listbox', vMultiselectListbox)
import Paginate from './components/elements/Paginate.vue'
Vue.component('paginate', Paginate)
import matcherModal from './components/elements/Modal.vue'
Vue.component('m-modal', matcherModal)
Vue.component('RecycleScroller', RecycleScroller)
// Import state and config
import state from './store/state.js'

import config from './assets/js/handle-config.js'
Vue.prototype.$config = config

// Import Install and register helper items
Vue.filter('count', count)
Vue.filter('domain', domain)
Vue.filter('prettyDate', prettyDate)
Vue.filter('pluralize', pluralize)
Vue.filter('deliveryName', deliveryName)
Vue.filter('orderStatusName', orderStatusName)
Vue.filter('orderStateColor', orderStateColor)
Vue.filter('shippingStatusName', shippingStatusName)
Vue.filter('shippingStateColor', shippingStateColor)
Vue.filter('shippingVendorName', shippingVendorName)
Vue.filter('tradeName', tradeName)
Vue.filter('cityCode', cityCode)
Vue.filter('disItemSrcType', disItemSrcType)
Vue.filter('disItemStatus', disItemStatus)
Vue.filter('disItemActionType', disItemActionType)

Vue.use(VueRouter)
Vue.use(VueTimepicker)

// process.env.brand 是 nidin 或 jkos，且 process.env.mode 是 prod 時，才會啟用
if ((process.env.brand === 'nidin' || process.env.brand === 'jkos') && (process.env.mode === 'prod' || process.env.mode === 'prebeta')) {
  let gtag = {
    config: { id: 'G-FLWMCT2Y2J' }
  }
  Vue.use(VueGtag, gtag)
}

// Routing logic
var router = new VueRouter({
  routes: routes,
  mode: 'history',
  scrollBehavior: function (to, from, savedPosition) {
    // 保持頁面位置
    if (to.params.savedPosition) return {}
    return savedPosition || { x: 0, y: 0 }
  }
})

// BDMS 已登入狀態跳轉到本站時，利用共用 cookie 裡的 token 確認登入資訊。
async function syncTokenFromCookie () {
  const cookieToken = getSharedToken()
  if (!cookieToken) return

  store.dispatch('setAuthError', '') // 清掉上一次的錯誤訊息

  let data = null
  let tokenInvalid = false
  try {
    const response = await axios({
      baseURL: config.baseURL,
      method: 'post',
      url: '/auth/checkToken',
      headers: {
        'MC-API-Token': cookieToken
      },
      // 後端沒回應時不能讓使用者一直卡在空白頁
      timeout: 5000
    })
    data = response.data
    if (!data || data.status !== 200) {
      tokenInvalid = TOKEN_INVALID_CODES.includes(data && data.status)
    }
  } catch (error) {
    tokenInvalid = isTokenInvalid(error)
  }

  // 兩種失敗都交給 guard 導去 /login
  if (!data || data.status !== 200) {
    if (tokenInvalid) {
      // token 確定無效刪 cookie，並通知使用者登入已失效
      removeSharedToken()
      Vue.notify({
        title: '登入已失效',
        text: '您的登入已失效或權限不足，請重新登入。',
        duration: 5000,
        type: 'danger',
        width: 400
      })
    } else {
      // 逾時、500、網路斷線不刪 cookie 此狀況不知道 token 還是否有效
      // 這個setAuthError是設定登入頁的紅字訊息，也是登入頁的不自動跳轉頁面的依據
      store.dispatch('setAuthError', '系統暫時無法回應，請稍後再試或重新登入。')
    }
    return
  }

  window.localStorage.setItem(config.token, cookieToken)
  window.localStorage.setItem(config.userId, data.user_id)
  window.localStorage.setItem(config.roleType, data.role_type)
  if (data.user_name) window.localStorage.setItem(config.account, data.user_name)
  store.dispatch('setUserInfo', {
    userId: data.user_id,
    token: cookieToken,
    roleType: data.role_type,
    account: data.user_name || ''
  })
}

router.beforeEach(async (to, from, next) => {
  const needsAuth = to.matched.some((record) => record.meta.requiresAuth)
  // 只有「這頁要登入、但本機沒有登入資料」時才去問後端，其餘路徑（含 /login）不用等
  if (needsAuth && !state.token && !window.localStorage.getItem(config.token)) {
    await syncTokenFromCookie()
  }
  const isLogin = state.token || window.localStorage.getItem(config.token)
  if (needsAuth) {
    if (isLogin) {
      if (config.brand === 'jkos') {
        if (to.meta.isShowInJkos) {
          if (to.name === 'dashboard') {
            next('/menuDetailTable')
          } else {
            next()
          }
        } else {
          next({ name: 'NotFound' })
        }
      } else {
        if (to.name === 'upJoinMenu' && from.name === 'menuInfo') {
          next({
            path: '/' + to.name + '/' + from.params.brand_id + '/' + from.params.menu_id + '/ok'
          })
        } else {
          next()
        }
      }
    } else {
      // 記住原本要去的網址，登入成功後再導回去。
      // 根路徑不記：帶了會蓋掉登入頁照 roleType 決定首頁的邏輯（POS 管理者是 /mqttTable）
      const login = { path: '/login' }
      if (to.fullPath !== '/') login.query = { redirect: to.fullPath }
      next(login)
    }
  } else {
    next()
  }
})
sync(store, router)

// Start out app!
// eslint-disable-next-line no-new
new Vue({
  el: '#root',
  router: router,
  store: store,
  render: h => h(AppView)
})

// Check local storage to handle refreshes
if (window.localStorage) {
  var localUserString = window.localStorage.getItem('user') || 'null'
  var localUser = JSON.parse(localUserString)

  if (localUser && store.state.user !== localUser) {
    store.commit('SET_USER', localUser)
    store.commit('SET_TOKEN', window.localStorage.getItem('token'))
  }
}



// WEBPACK FOOTER //
// ./src/main.js